import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import { showToast } from "../helper/notifications";
import {
  GoogleMap,
  Autocomplete
} from "@react-google-maps/api";
import { useGoogleMaps } from "../context/GoogleMapsContext";
import DrawTools from "../components/DrawTools";
import PageHeader from "../components/common/PageHeader";
import FloatingFormActions from "../components/common/FloatingFormActions";
import { Icon } from "@iconify/react";

/* ================= THEME CONSTANTS ================= */
const DEFAULT_CENTER = { lat: 48.137, lng: 11.575 };

const TABS = [
  { id: "Identity", icon: "solar:shop-2-linear" },
  { id: "Main Location Categories", icon: "solar:tag-linear" },
  { id: "Sub Location Categories", icon: "solar:tag-linear" },
  { id: "Geofence and Boundaries Outline", icon: "tabler:polygon" },
];

const LocationsTopFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoaded } = useGoogleMaps();

  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Tab Navigation state
  const [activeTab, setActiveTab] = useState("Identity");
  const tabIndex = TABS.findIndex((t) => t.id === activeTab);

  // Drawing & Nudge controls state
  const [outlineDrawVersion, setOutlineDrawVersion] = useState(0);
  const [coordStep, setCoordStep] = useState(0.00001);
  const [activePolyIndex, setActivePolyIndex] = useState(0);
  const [selectedVertexIndex, setSelectedVertexIndex] = useState(null);
  const [showJsonRaw, setShowJsonRaw] = useState(false);

  // Categories state (Main Locations)
  const [allCategories, setAllCategories] = useState([]);
  const [categorySearch, setCategorySearch] = useState("");
  const [selectedGroupFilter, setSelectedGroupFilter] = useState("All");

  // Categories state (Sub Locations)
  const [allSubCategories, setAllSubCategories] = useState([]);
  const [subCategorySearch, setSubCategorySearch] = useState("");
  const [selectedSubGroupFilter, setSelectedSubGroupFilter] = useState("All");

  // Form & Loading State
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    geometrics_outline: "",
    preset_categories: [],
    preset_sublocation_categories: []
  });

  const isTabCompleted = (tabId) => {
    switch (tabId) {
      case "Identity":
        return Boolean(formData.name?.trim());
      case "Main Location Categories":
        return Boolean(formData.preset_categories && formData.preset_categories.length > 0);
      case "Sub Location Categories":
        return Boolean(formData.preset_sublocation_categories && formData.preset_sublocation_categories.length > 0);
      case "Geofence and Boundaries Outline":
        return Boolean(formData.geometrics_outline && formData.geometrics_outline !== "[]" && formData.geometrics_outline !== "");
      default:
        return false;
    }
  };

  const parsePresetCategories = useCallback((val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return typeof val === "string" ? val.split(",").map((s) => s.trim()).filter(Boolean) : [];
    }
  }, []);

  const hasFormChanged = () => {
    if (!initialData) {
      return Boolean(
        formData.name ||
        formData.description ||
        formData.geometrics_outline ||
        (formData.preset_categories && formData.preset_categories.length > 0) ||
        (formData.preset_sublocation_categories && formData.preset_sublocation_categories.length > 0)
      );
    }
    const currentCats = formData.preset_categories || [];
    const initCats = initialData.preset_categories || [];
    const catsChanged =
      currentCats.length !== initCats.length ||
      currentCats.some((c) => !initCats.includes(c));

    const currentSubCats = formData.preset_sublocation_categories || [];
    const initSubCats = initialData.preset_sublocation_categories || [];
    const subCatsChanged =
      currentSubCats.length !== initSubCats.length ||
      currentSubCats.some((c) => !initSubCats.includes(c));

    return (
      (formData.name || "") !== (initialData.name || "") ||
      (formData.description || "") !== (initialData.description || "") ||
      (formData.geometrics_outline || "") !== (initialData.geometrics_outline || "") ||
      catsChanged ||
      subCatsChanged
    );
  };

  // Helper to parse polygon GeoJSON / format
  const parsePolygons = useCallback((geoJson) => {
    if (!geoJson) return [];
    try {
      const parsed = typeof geoJson === "string" ? JSON.parse(geoJson) : geoJson;
      if (Array.isArray(parsed) && parsed.length && parsed[0].path) {
        return parsed.map((p) => (p.path || []).map((pt) => ({ lat: Number(pt.lat) || 0, lng: Number(pt.lng) || 0 })));
      }
      if (Array.isArray(parsed) && parsed.every((p) => Array.isArray(p))) {
        return parsed.filter((p) => p.length).map((poly) => poly.map((pt) => ({ lat: pt.lat !== undefined ? pt.lat : 0, lng: pt.lng !== undefined ? pt.lng : 0 })));
      }
      if (Array.isArray(parsed) && parsed.length && parsed[0].lat !== undefined) {
        return [parsed.map((pt) => ({ lat: pt.lat !== undefined ? pt.lat : 0, lng: pt.lng !== undefined ? pt.lng : 0 }))];
      }
      return [];
    } catch {
      return [];
    }
  }, []);

  // 1. Logic to focus map on existing boundaries
  const fitMapToBoundary = useCallback((outlineData) => {
    if (!outlineData || !mapRef.current) return;
    try {
      const parsed = typeof outlineData === "string" ? JSON.parse(outlineData) : outlineData;
      if (!Array.isArray(parsed)) return;

      const bounds = new window.google.maps.LatLngBounds();
      let hasCoords = false;

      parsed.forEach((item) => {
        const path = Array.isArray(item) ? item : item.path;
        if (Array.isArray(path)) {
          path.forEach((pt) => {
            if (pt.lat && pt.lng) {
              bounds.extend({ lat: parseFloat(pt.lat), lng: parseFloat(pt.lng) });
              hasCoords = true;
            }
          });
        }
      });

      if (hasCoords) {
        mapRef.current.fitBounds(bounds, 50);
      }
    } catch (e) {
      console.warn("Boundary zoom failed:", e);
    }
  }, []);

  // Fetch categories lists on mount
  useEffect(() => {
    api.get("/main-locations/categories")
      .then((res) => {
        setAllCategories(res.data?.data || []);
      })
      .catch((err) => console.warn("Failed to load main location categories:", err));

    api.get("/options/sublocation-types")
      .then((res) => {
        setAllSubCategories(res.data || []);
      })
      .catch((err) => console.warn("Failed to load sublocation categories:", err));
  }, []);

  // 2. Fetch data if editing
  useEffect(() => {
    if (id && isLoaded) loadData();
  }, [id, isLoaded]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/top-locations/${id}`);
      const data = res.data?.user || res.data;

      const outline = typeof data.geometrics_outline === "object"
        ? JSON.stringify(data.geometrics_outline)
        : data.geometrics_outline;

      const loadedObj = {
        name: data.name || "",
        description: data.description || "",
        geometrics_outline: outline || "",
        preset_categories: parsePresetCategories(data.preset_categories),
        preset_sublocation_categories: parsePresetCategories(data.preset_sublocation_categories)
      };

      setFormData(loadedObj);
      setInitialData(loadedObj);

      setOutlineDrawVersion((v) => v + 1);
      setTimeout(() => fitMapToBoundary(outline), 600);
    } catch (error) {
      console.error("Load error:", error);
      showToast("error", "Failed to load location data");
    } finally {
      setLoading(false);
    }
  };

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    try {
      map.setTilt(0);
      map.setMapTypeId("satellite");
    } catch (e) {}

    if (formData.geometrics_outline) {
      setTimeout(() => fitMapToBoundary(formData.geometrics_outline), 300);
    }
  }, [formData.geometrics_outline, fitMapToBoundary]);

  useEffect(() => {
    if (activeTab === "Geofence and Boundaries Outline" && mapRef.current) {
      setTimeout(() => {
        if (window.google?.maps?.event && mapRef.current) {
          window.google.maps.event.trigger(mapRef.current, "resize");
        }
        try {
          mapRef.current.setTilt(0);
          mapRef.current.setMapTypeId("satellite");
        } catch (e) {}
        if (formData.geometrics_outline) {
          fitMapToBoundary(formData.geometrics_outline);
        }
      }, 150);
    }
  }, [activeTab, fitMapToBoundary, formData.geometrics_outline]);

  const onPlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    if (place?.geometry && mapRef.current) {
      if (place.geometry.viewport) {
        mapRef.current.fitBounds(place.geometry.viewport);
      } else {
        mapRef.current.panTo(place.geometry.location);
        mapRef.current.setZoom(16);
      }
    }
  };

  // Adjust coordinate values via Nudge +/-
  const adjustCoordinate = (polyIdx, ptIdx, field, delta) => {
    const polys = parsePolygons(formData.geometrics_outline);
    if (!polys[polyIdx] || !polys[polyIdx][ptIdx]) return;

    const currentVal = parseFloat(polys[polyIdx][ptIdx][field]) || 0;
    const newVal = Number((currentVal + delta).toFixed(7));

    const updatedPolys = polys.map((poly, pIdx) => {
      if (pIdx !== polyIdx) return poly;
      return poly.map((pt, idx) => {
        if (idx !== ptIdx) return pt;
        return {
          ...pt,
          [field]: newVal
        };
      });
    });

    setFormData((prev) => ({
      ...prev,
      geometrics_outline: JSON.stringify(updatedPolys)
    }));
  };

  // Handle direct input change
  const handleCoordinateChange = (polyIdx, ptIdx, field, val) => {
    const polys = parsePolygons(formData.geometrics_outline);
    if (!polys[polyIdx] || !polys[polyIdx][ptIdx]) return;

    const updatedPolys = polys.map((poly, pIdx) => {
      if (pIdx !== polyIdx) return poly;
      return poly.map((pt, idx) => {
        if (idx !== ptIdx) return pt;
        return {
          ...pt,
          [field]: val
        };
      });
    });

    setFormData((prev) => ({
      ...prev,
      geometrics_outline: JSON.stringify(updatedPolys)
    }));
  };

  // Sanitize on blur
  const handleCoordinateBlur = (polyIdx, ptIdx, field) => {
    const polys = parsePolygons(formData.geometrics_outline);
    if (!polys[polyIdx] || !polys[polyIdx][ptIdx]) return;

    const rawVal = polys[polyIdx][ptIdx][field];
    const num = parseFloat(rawVal);
    if (!isNaN(num)) {
      const updatedPolys = polys.map((poly, pIdx) => {
        if (pIdx !== polyIdx) return poly;
        return poly.map((pt, idx) => {
          if (idx !== ptIdx) return pt;
          return {
            ...pt,
            [field]: Number(num.toFixed(7))
          };
        });
      });
      setFormData((prev) => ({
        ...prev,
        geometrics_outline: JSON.stringify(updatedPolys)
      }));
    }
  };

  // Delete a point from current active polygon
  const handleRemovePoint = (polyIdx, ptIdx) => {
    const polys = parsePolygons(formData.geometrics_outline);
    if (!polys[polyIdx]) return;

    const updatedPath = polys[polyIdx].filter((_, idx) => idx !== ptIdx);
    let updatedPolys;
    if (updatedPath.length === 0) {
      updatedPolys = polys.filter((_, idx) => idx !== polyIdx);
    } else {
      updatedPolys = polys.map((p, idx) => (idx === polyIdx ? updatedPath : p));
    }

    setFormData((prev) => ({
      ...prev,
      geometrics_outline: updatedPolys.length > 0 ? JSON.stringify(updatedPolys) : ""
    }));
    if (selectedVertexIndex === ptIdx) {
      setSelectedVertexIndex(null);
    }
  };

  // Add a vertex or create starter polygon
  const handleAddPoint = (polyIdx = 0) => {
    const polys = parsePolygons(formData.geometrics_outline);
    let updatedPolys;

    if (polys.length === 0) {
      // Determine center from map or DEFAULT_CENTER
      let centerLat = DEFAULT_CENTER.lat;
      let centerLng = DEFAULT_CENTER.lng;

      if (mapRef.current?.getCenter) {
        const c = mapRef.current.getCenter();
        if (c) {
          centerLat = c.lat();
          centerLng = c.lng();
        }
      }

      const delta = 0.001; // ~100m starter box for top location area
      const initialBox = [
        { lat: Number((centerLat + delta).toFixed(7)), lng: Number((centerLng - delta).toFixed(7)) },
        { lat: Number((centerLat + delta).toFixed(7)), lng: Number((centerLng + delta).toFixed(7)) },
        { lat: Number((centerLat - delta).toFixed(7)), lng: Number((centerLng + delta).toFixed(7)) },
        { lat: Number((centerLat - delta).toFixed(7)), lng: Number((centerLng - delta).toFixed(7)) }
      ];
      updatedPolys = [initialBox];

      setTimeout(() => fitMapToBoundary(JSON.stringify(updatedPolys)), 200);
    } else {
      const targetPoly = polys[polyIdx] || polys[0];
      if (targetPoly.length === 0) {
        let centerLat = DEFAULT_CENTER.lat;
        let centerLng = DEFAULT_CENTER.lng;
        if (mapRef.current?.getCenter) {
          const c = mapRef.current.getCenter();
          if (c) {
            centerLat = c.lat();
            centerLng = c.lng();
          }
        }
        const newPt = { lat: Number(centerLat.toFixed(7)), lng: Number(centerLng.toFixed(7)) };
        updatedPolys = polys.map((p, idx) => (idx === polyIdx ? [newPt] : p));
      } else {
        const lastPt = targetPoly[targetPoly.length - 1];
        const newPt = {
          lat: Number((Number(lastPt.lat) + 0.0001).toFixed(7)),
          lng: Number((Number(lastPt.lng) + 0.0001).toFixed(7))
        };
        updatedPolys = polys.map((p, idx) => (idx === polyIdx ? [...p, newPt] : p));
      }
    }

    setFormData((prev) => ({
      ...prev,
      geometrics_outline: JSON.stringify(updatedPolys)
    }));
  };

  // Add an additional shape (polygon)
  const handleAddShape = () => {
    const polys = parsePolygons(formData.geometrics_outline);

    let centerLat = DEFAULT_CENTER.lat;
    let centerLng = DEFAULT_CENTER.lng;

    if (mapRef.current?.getCenter) {
      const c = mapRef.current.getCenter();
      if (c) {
        centerLat = c.lat();
        centerLng = c.lng();
      }
    }

    const offset = polys.length * 0.0004;
    const delta = 0.0008; // ~80m starter square

    const newBox = [
      { lat: Number((centerLat + offset + delta).toFixed(7)), lng: Number((centerLng + offset - delta).toFixed(7)) },
      { lat: Number((centerLat + offset + delta).toFixed(7)), lng: Number((centerLng + offset + delta).toFixed(7)) },
      { lat: Number((centerLat + offset - delta).toFixed(7)), lng: Number((centerLng + offset + delta).toFixed(7)) },
      { lat: Number((centerLat + offset - delta).toFixed(7)), lng: Number((centerLng + offset - delta).toFixed(7)) }
    ];

    const updatedPolys = [...polys, newBox];
    const newIndex = updatedPolys.length - 1;

    setFormData((prev) => ({
      ...prev,
      geometrics_outline: JSON.stringify(updatedPolys)
    }));
    setActivePolyIndex(newIndex);
    setSelectedVertexIndex(0);
    setOutlineDrawVersion((v) => v + 1);

    if (mapRef.current) {
      mapRef.current.panTo({ lat: centerLat + offset, lng: centerLng + offset });
    }
  };

  const handleClearBoundary = () => {
    setFormData((prev) => ({ ...prev, geometrics_outline: "" }));
    setOutlineDrawVersion((v) => v + 1);
    setSelectedVertexIndex(null);
  };

  // Category selection handlers (Main Locations)
  const handleToggleCategory = (catLabel) => {
    setFormData((prev) => {
      const current = prev.preset_categories || [];
      const exists = current.includes(catLabel);
      const updated = exists ? current.filter((c) => c !== catLabel) : [...current, catLabel];
      return { ...prev, preset_categories: updated };
    });
  };

  const handleRemoveCategory = (catLabel) => {
    setFormData((prev) => ({
      ...prev,
      preset_categories: (prev.preset_categories || []).filter((c) => c !== catLabel)
    }));
  };

  const handleClearAllCategories = () => {
    setFormData((prev) => ({ ...prev, preset_categories: [] }));
  };

  // Sub-Category selection handlers (Sub Locations)
  const handleToggleSubCategory = (catLabel) => {
    setFormData((prev) => {
      const current = prev.preset_sublocation_categories || [];
      const exists = current.includes(catLabel);
      const updated = exists ? current.filter((c) => c !== catLabel) : [...current, catLabel];
      return { ...prev, preset_sublocation_categories: updated };
    });
  };

  const handleRemoveSubCategory = (catLabel) => {
    setFormData((prev) => ({
      ...prev,
      preset_sublocation_categories: (prev.preset_sublocation_categories || []).filter((c) => c !== catLabel)
    }));
  };

  const handleClearAllSubCategories = () => {
    setFormData((prev) => ({ ...prev, preset_sublocation_categories: [] }));
  };

  // Quick save outline button inside outline tab
  const handleSaveOutline = async () => {
    if (!id) {
      handleSubmit();
      return;
    }
    setSaveLoading(true);
    try {
      await api.put(`/top-locations/${id}`, {
        name: formData.name.trim(),
        description: formData.description?.trim() || null,
        geometrics_outline: formData.geometrics_outline || null,
        preset_categories: formData.preset_categories || [],
        preset_sublocation_categories: formData.preset_sublocation_categories || []
      });
      showToast("success", "Geofence boundary saved successfully!");
      setInitialData({ ...formData });
    } catch (err) {
      console.error("Save outline error:", err);
      showToast("error", "Failed to save outline.");
    } finally {
      setSaveLoading(false);
    }
  };

  // Submit full form
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!formData.name.trim()) {
      showToast("error", "Please enter a location name.");
      return;
    }

    setSaveLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description?.trim() || null,
        geometrics_outline: formData.geometrics_outline || null,
        preset_categories: formData.preset_categories || [],
        preset_sublocation_categories: formData.preset_sublocation_categories || []
      };

      if (id) {
        await api.put(`/top-locations/${id}`, payload);
        showToast("success", "Top Location updated successfully!");
        setInitialData({ ...formData });
      } else {
        await api.post("/top-locations", payload);
        showToast("success", "Top Location created successfully!");
        navigate("/locations-top");
      }
    } catch (error) {
      console.error("Save operation failed:", error);
      showToast("error", error.response?.data?.message || "Save operation failed.");
    } finally {
      setSaveLoading(false);
    }
  };

  const allPolys = parsePolygons(formData.geometrics_outline);
  const activeIndex = (activePolyIndex < allPolys.length && activePolyIndex >= 0) ? activePolyIndex : 0;
  const currentPoints = allPolys[activeIndex] || [];

  const distinctGroups = useMemo(() => {
    const groups = new Set();
    allCategories.forEach((c) => {
      if (c.category_group) groups.add(c.category_group);
    });
    return ["All", ...Array.from(groups).sort()];
  }, [allCategories]);

  const filteredCategories = useMemo(() => {
    const search = categorySearch.toLowerCase().trim();
    return allCategories.filter((cat) => {
      const matchGroup = selectedGroupFilter === "All" || cat.category_group === selectedGroupFilter;
      if (!matchGroup) return false;
      if (!search) return true;
      return (
        (cat.label_de && cat.label_de.toLowerCase().includes(search)) ||
        (cat.label && cat.label.toLowerCase().includes(search)) ||
        (cat.type_key && cat.type_key.toLowerCase().includes(search)) ||
        (cat.category_group && cat.category_group.toLowerCase().includes(search))
      );
    });
  }, [allCategories, categorySearch, selectedGroupFilter]);

  const distinctSubGroups = useMemo(() => {
    const groups = new Set();
    allSubCategories.forEach((c) => {
      if (c.category_group) groups.add(c.category_group);
    });
    return ["All", ...Array.from(groups).sort()];
  }, [allSubCategories]);

  const filteredSubCategories = useMemo(() => {
    const search = subCategorySearch.toLowerCase().trim();
    return allSubCategories.filter((cat) => {
      const matchGroup = selectedSubGroupFilter === "All" || cat.category_group === selectedSubGroupFilter;
      if (!matchGroup) return false;
      if (!search) return true;
      return (
        (cat.label_de && cat.label_de.toLowerCase().includes(search)) ||
        (cat.label && cat.label.toLowerCase().includes(search)) ||
        (cat.type_key && cat.type_key.toLowerCase().includes(search)) ||
        (cat.category_group && cat.category_group.toLowerCase().includes(search))
      );
    });
  }, [allSubCategories, subCategorySearch, selectedSubGroupFilter]);

  return (
    <div className="locations-top-form-page position-relative pb-80">
      {/* CLEAN BACK BUTTON */}
      <button
        type="button"
        onClick={() => navigate("/locations-top")}
        className="btn btn-link p-0 text-secondary-light text-decoration-none d-inline-flex align-items-center gap-2 mb-16 hover-text-primary-600 transition-all"
      >
        <Icon icon="solar:arrow-left-linear" className="text-xl" />
        <span className="fw-medium text-sm">Back to Top Locations</span>
      </button>

      {/* HEADER SECTION */}
      <PageHeader
        title={id ? `Edit Top Location: ${formData.name || "Profile"}` : "Add New Top Location"}
        subtitle="Master Data & Geofence"
      />

      <form onSubmit={handleSubmit} noValidate className="mb-80 form-has-floating-actions">
        <div className="card radius-12 border-0 shadow-none bg-base overflow-hidden">
          <div className="d-flex flex-column flex-lg-row">

            {/* NAVIGATION SIDEBAR WITH COMPLETION BADGES */}
            <div className="bg-neutral-50 border-end" style={{ minWidth: "270px", maxWidth: "270px" }}>
              <div className="p-16">
                <div className="d-flex flex-column gap-1">
                  {TABS.map((tab) => {
                    const completed = isTabCompleted(tab.id);
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        className={`d-flex align-items-center justify-content-between px-16 py-12 radius-8 border-0 transition-all text-start w-100 ${
                          isActive
                            ? "bg-primary-600 text-white shadow-sm fw-bold"
                            : "bg-transparent text-secondary-light hover-bg-neutral-200"
                        }`}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <Icon
                            icon={tab.icon}
                            className={`text-xl ${isActive ? "text-white" : "text-primary-600"}`}
                          />
                          <span className="text-sm">{tab.id}</span>
                        </div>
                        {completed && (
                          <span
                            className={`badge ${
                              isActive
                                ? "bg-white text-primary-600"
                                : "bg-success-50 text-success-600 border border-success-200"
                            } radius-circle p-4`}
                            title="Filled"
                          >
                            <Icon icon="solar:check-circle-bold" className="text-xs" />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

              </div>
            </div>

            {/* FORM CONTENT */}
            <div className="flex-grow-1 p-24 p-lg-32" style={{ minHeight: "75vh" }}>
              <div className="d-flex align-items-center justify-content-between mb-24 border-bottom pb-14">
                <h5 className="fw-bold text-primary-light mb-0">{activeTab}</h5>
                <span className="text-xs text-secondary-light">
                  Section {tabIndex + 1} of {TABS.length}
                </span>
              </div>

              {/* TAB 1: Identity */}
              {activeTab === "Identity" && (
                <div>
                  <div className="d-flex align-items-center gap-2 mb-20 pb-12 border-bottom">
                    <div className="w-36-px h-36-px bg-primary-50 text-primary-600 radius-8 d-flex align-items-center justify-content-center">
                      <Icon icon="solar:shop-2-bold" className="text-xl" />
                    </div>
                    <div>
                      <h6 className="text-sm fw-bold mb-0">Identity</h6>
                      <p className="text-xs text-secondary-light mb-0">
                        Define the name and optional description for this top-level geofenced location.
                      </p>
                    </div>
                  </div>

                  <div className="row g-4 mb-24">
                    <div className="col-md-5">
                      <label className="form-label text-xs fw-semibold text-dark mb-1">
                        Location Name <span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control radius-8 text-sm"
                        placeholder="e.g. Olympia-Einkaufszentrum, City Center West"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>

                    <div className="col-md-7">
                      <label className="form-label text-xs fw-semibold text-dark mb-1">
                        Description
                      </label>
                      <input
                        type="text"
                        className="form-control radius-8 text-sm"
                        placeholder="Brief description or notes about this top location..."
                        value={formData.description || ""}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Summary / Status Cards */}
                  <div className="row g-3">
                    <div className="col-sm-6 col-xl-3">
                      <div className="p-16 radius-8 bg-neutral-50 border h-100">
                        <div className="d-flex align-items-center gap-2 text-xs text-secondary-light mb-1">
                          <Icon icon="solar:tag-linear" className="text-primary-600 text-sm" />
                          <span>Main Categories</span>
                        </div>
                        <div className="text-sm fw-bold text-dark">
                          {(formData.preset_categories || []).length} assigned
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-xl-3">
                      <div className="p-16 radius-8 bg-neutral-50 border h-100">
                        <div className="d-flex align-items-center gap-2 text-xs text-secondary-light mb-1">
                          <Icon icon="solar:tag-linear" className="text-primary-600 text-sm" />
                          <span>Sub Categories</span>
                        </div>
                        <div className="text-sm fw-bold text-dark">
                          {(formData.preset_sublocation_categories || []).length} assigned
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 col-xl-3">
                      <div className="p-16 radius-8 bg-neutral-50 border h-100">
                        <div className="d-flex align-items-center gap-2 text-xs text-secondary-light mb-1">
                          <Icon icon="tabler:polygon" className="text-primary-600 text-sm" />
                          <span>Geofence Boundary</span>
                        </div>
                        <div className="text-sm fw-bold text-dark">
                          {allPolys.length > 0 ? `${allPolys.length} Shape(s) defined` : "Not defined yet"}
                        </div>
                      </div>
                    </div>
                    {id && (
                      <div className="col-sm-6 col-xl-3">
                        <div className="p-16 radius-8 bg-neutral-50 border h-100">
                          <div className="d-flex align-items-center gap-2 text-xs text-secondary-light mb-1">
                            <Icon icon="solar:database-linear" className="text-primary-600 text-sm" />
                            <span>Record ID</span>
                          </div>
                          <div className="text-sm fw-bold font-monospace text-dark">
                            #{id}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* TAB 2: Main Location Categories */}
              {activeTab === "Main Location Categories" && (
                <div>
                  <div className="d-flex align-items-center justify-content-between mb-16 pb-12 border-bottom flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <div className="w-36-px h-36-px bg-primary-50 text-primary-600 radius-8 d-flex align-items-center justify-content-center">
                  <Icon icon="solar:tag-bold" className="text-xl" />
                </div>
                <div>
                  <h6 className="text-sm fw-bold mb-0">Main Location Categories</h6>
                  <p className="text-xs text-secondary-light mb-0">
                    Pick categories for this top location. These categories appear first in the dropdowns when configuring Main Locations.
                  </p>
                </div>
              </div>

              <div className="d-flex align-items-center gap-2">
                <span className="badge bg-primary-subtle text-primary-700 px-10 py-6 radius-6 text-xs fw-semibold">
                  {(formData.preset_categories || []).length} Selected
                </span>
                {(formData.preset_categories || []).length > 0 && (
                  <button
                    type="button"
                    onClick={handleClearAllCategories}
                    className="btn btn-xs btn-outline-danger py-1 px-8 radius-4 text-xs d-inline-flex align-items-center gap-1"
                  >
                    <Icon icon="solar:trash-bin-trash-linear" />
                    <span>Clear All</span>
                  </button>
                )}
              </div>
            </div>

            {/* Selected Categories Tags */}
            <div className="mb-16">
              <label className="form-label text-xs fw-semibold text-secondary-light mb-2 d-block">
                Selected Main Location Categories:
              </label>
              {(formData.preset_categories || []).length > 0 ? (
                <div className="d-flex flex-wrap gap-2 p-12 bg-neutral-50 radius-8 border" style={{ maxHeight: "130px", overflowY: "auto" }}>
                  {(formData.preset_categories || []).map((catLabel) => {
                    const fullCat = allCategories.find((c) => c.label_de === catLabel || c.type_key === catLabel);
                    return (
                      <span
                        key={catLabel}
                        className="badge bg-white text-primary-700 border border-primary-200 px-10 py-6 radius-6 text-xs fw-medium d-inline-flex align-items-center gap-2 shadow-xs"
                      >
                        <span className="text-warning-600">★</span>
                        <span>{catLabel}</span>
                        {fullCat?.label && fullCat.label !== catLabel && (
                          <span className="text-secondary-light opacity-75 font-monospace text-xxs">({fullCat.label})</span>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveCategory(catLabel)}
                          className="btn btn-link p-0 text-danger-500 hover-text-danger-700 text-decoration-none d-flex align-items-center ms-1"
                          title={`Remove ${catLabel}`}
                        >
                          <Icon icon="solar:close-circle-bold" style={{ fontSize: "14px" }} />
                        </button>
                      </span>
                    );
                  })}
                </div>
              ) : (
                <div className="p-12 bg-neutral-50 radius-8 border text-xs text-secondary-light d-flex align-items-center gap-2">
                  <Icon icon="solar:info-circle-linear" className="text-primary-600 text-base" />
                  <span>No categories selected yet. Use the search filter and checkboxes below to select categories for this location.</span>
                </div>
              )}
            </div>

            {/* Filter and Search Bar */}
            <div className="row g-2 align-items-center mb-12">
              <div className="col-md-6 col-lg-5">
                <div className="position-relative">
                  <span className="position-absolute top-50 start-0 translate-middle-y ms-12 text-secondary-light text-sm pointer-events-none d-flex align-items-center">
                    <Icon icon="solar:magnifer-linear" />
                  </span>
                  <input
                    type="text"
                    className="form-control ps-36 radius-8 text-xs py-2"
                    placeholder="Search categories..."
                    value={categorySearch}
                    onChange={(e) => setCategorySearch(e.target.value)}
                  />
                  {categorySearch && (
                    <button
                      type="button"
                      onClick={() => setCategorySearch("")}
                      className="btn btn-link position-absolute top-50 end-0 translate-middle-y me-8 p-0 text-secondary-light text-decoration-none"
                    >
                      <Icon icon="solar:close-circle-linear" style={{ fontSize: "14px" }} />
                    </button>
                  )}
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="d-flex align-items-center gap-2">
                  <span className="text-xs text-secondary-light text-nowrap">Group:</span>
                  <select
                    className="form-select radius-8 text-xs py-2"
                    value={selectedGroupFilter}
                    onChange={(e) => setSelectedGroupFilter(e.target.value)}
                  >
                    {distinctGroups.map((grp) => (
                      <option key={grp} value={grp}>
                        {grp}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="col-12 col-lg-3 text-lg-end">
                <span className="text-xs text-secondary-light">
                  Showing {filteredCategories.length} of {allCategories.length} categories
                </span>
              </div>
            </div>

            {/* Categories Multi-Select Grid */}
            <div
              className="border radius-8 p-12 bg-white overflow-y-auto"
              style={{ maxHeight: "340px", scrollbarWidth: "thin" }}
            >
              {filteredCategories.length === 0 ? (
                <div className="py-24 text-center text-secondary-light text-xs">
                  <Icon icon="solar:sad-circle-linear" className="text-2xl mb-1 d-block mx-auto text-neutral-400" />
                  No categories match "{categorySearch}". Try a different keyword or group filter.
                </div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                    gap: "12px",
                  }}
                >
                  {filteredCategories.map((cat) => {
                    const catVal = cat.label_de || cat.label;
                    const isChecked = (formData.preset_categories || []).includes(catVal);
                    return (
                      <div
                        key={cat.id || cat.type_key}
                        onClick={() => handleToggleCategory(catVal)}
                        className={`p-10 radius-8 border transition-all cursor-pointer d-flex align-items-start gap-2 ${
                          isChecked
                            ? "bg-primary-50 border-primary-400 text-primary-900 shadow-xs"
                            : "bg-white border-neutral-200 hover-bg-neutral-50 text-dark"
                        }`}
                        style={{ userSelect: "none", minWidth: 0, overflow: "hidden" }}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input mt-1 flex-shrink-0 cursor-pointer"
                          checked={isChecked}
                          onChange={() => {}}
                        />
                        <div className="flex-grow-1" style={{ minWidth: 0, overflow: "hidden" }}>
                          <div className="d-flex align-items-center justify-content-between gap-1 mb-1">
                            <span
                              className="text-xs fw-semibold text-truncate d-block"
                              style={{ minWidth: 0 }}
                              title={cat.label_de || cat.label}
                            >
                              {cat.label_de || cat.label}
                            </span>
                            {isChecked && (
                              <span className="text-primary-600 flex-shrink-0 ms-1" title="Selected">
                                <Icon icon="solar:check-circle-bold" className="text-sm" />
                              </span>
                            )}
                          </div>
                          <div
                            className="text-xxs text-secondary-light text-truncate mb-6"
                            style={{ minWidth: 0 }}
                            title={cat.label}
                          >
                            {cat.label}
                          </div>
                          {cat.category_group && (
                            <div>
                              <span
                                className="badge bg-neutral-100 text-secondary-light px-6 py-2 radius-4 text-xxs d-inline-block text-truncate border border-neutral-200"
                                style={{ maxWidth: "100%", verticalAlign: "middle" }}
                                title={cat.category_group}
                              >
                                {cat.category_group}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
                </div>
              )}

              {/* TAB 3: Sub Location Categories */}
              {activeTab === "Sub Location Categories" && (
                <div>
                  <div className="d-flex align-items-center justify-content-between mb-16 pb-12 border-bottom flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <div className="w-36-px h-36-px bg-primary-50 text-primary-600 radius-8 d-flex align-items-center justify-content-center">
                        <Icon icon="solar:tag-bold" className="text-xl" />
                      </div>
                      <div>
                        <h6 className="text-sm fw-bold mb-0">Sub Location Categories</h6>
                        <p className="text-xs text-secondary-light mb-0">
                          Pick preset categories for sublocations in this top location. These categories appear first in the dropdowns when configuring Sub-Locations.
                        </p>
                      </div>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      <span className="badge bg-primary-subtle text-primary-700 px-10 py-6 radius-6 text-xs fw-semibold">
                        {(formData.preset_sublocation_categories || []).length} Selected
                      </span>
                      {(formData.preset_sublocation_categories || []).length > 0 && (
                        <button
                          type="button"
                          onClick={handleClearAllSubCategories}
                          className="btn btn-xs btn-outline-danger py-1 px-8 radius-4 text-xs d-inline-flex align-items-center gap-1"
                        >
                          <Icon icon="solar:trash-bin-trash-linear" />
                          <span>Clear All</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Selected Categories Tags */}
                  <div className="mb-16">
                    <label className="form-label text-xs fw-semibold text-secondary-light mb-2 d-block">
                      Selected Sub Location Categories:
                    </label>
                    {(formData.preset_sublocation_categories || []).length > 0 ? (
                      <div className="d-flex flex-wrap gap-2 p-12 bg-neutral-50 radius-8 border" style={{ maxHeight: "130px", overflowY: "auto" }}>
                        {(formData.preset_sublocation_categories || []).map((catLabel) => {
                          const fullCat = allSubCategories.find((c) => c.label_de === catLabel || c.type_key === catLabel);
                          return (
                            <span
                              key={catLabel}
                              className="badge bg-white text-primary-700 border border-primary-200 px-10 py-6 radius-6 text-xs fw-medium d-inline-flex align-items-center gap-2 shadow-xs"
                            >
                              <span className="text-warning-600">★</span>
                              <span>{catLabel}</span>
                              {fullCat?.label && fullCat.label !== catLabel && (
                                <span className="text-secondary-light opacity-75 font-monospace text-xxs">({fullCat.label})</span>
                              )}
                              <button
                                type="button"
                                onClick={() => handleRemoveSubCategory(catLabel)}
                                className="btn btn-link p-0 text-danger-500 hover-text-danger-700 text-decoration-none d-flex align-items-center ms-1"
                                title={`Remove ${catLabel}`}
                              >
                                <Icon icon="solar:close-circle-bold" style={{ fontSize: "14px" }} />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-12 bg-neutral-50 radius-8 border text-xs text-secondary-light d-flex align-items-center gap-2">
                        <Icon icon="solar:info-circle-linear" className="text-primary-600 text-base" />
                        <span>No sublocation categories selected yet. Use the search filter and checkboxes below to select preset categories for sublocations.</span>
                      </div>
                    )}
                  </div>

                  {/* Filter and Search Bar */}
                  <div className="row g-2 align-items-center mb-12">
                    <div className="col-md-6 col-lg-5">
                      <div className="position-relative">
                        <span className="position-absolute top-50 start-0 translate-middle-y ms-12 text-secondary-light text-sm pointer-events-none d-flex align-items-center">
                          <Icon icon="solar:magnifer-linear" />
                        </span>
                        <input
                          type="text"
                          className="form-control ps-36 radius-8 text-xs py-2"
                          placeholder="Search sublocation categories..."
                          value={subCategorySearch}
                          onChange={(e) => setSubCategorySearch(e.target.value)}
                        />
                        {subCategorySearch && (
                          <button
                            type="button"
                            onClick={() => setSubCategorySearch("")}
                            className="btn btn-link position-absolute top-50 end-0 translate-middle-y me-8 p-0 text-secondary-light text-decoration-none"
                          >
                            <Icon icon="solar:close-circle-linear" style={{ fontSize: "14px" }} />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="col-md-6 col-lg-4">
                      <div className="d-flex align-items-center gap-2">
                        <span className="text-xs text-secondary-light text-nowrap">Group:</span>
                        <select
                          className="form-select radius-8 text-xs py-2"
                          value={selectedSubGroupFilter}
                          onChange={(e) => setSelectedSubGroupFilter(e.target.value)}
                        >
                          {distinctSubGroups.map((grp) => (
                            <option key={grp} value={grp}>
                              {grp}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="col-12 col-lg-3 text-lg-end">
                      <span className="text-xs text-secondary-light">
                        Showing {filteredSubCategories.length} of {allSubCategories.length} categories
                      </span>
                    </div>
                  </div>

                  {/* Sub-Categories Multi-Select Grid */}
                  <div
                    className="border radius-8 p-12 bg-white overflow-y-auto"
                    style={{ maxHeight: "340px", scrollbarWidth: "thin" }}
                  >
                    {filteredSubCategories.length === 0 ? (
                      <div className="py-24 text-center text-secondary-light text-xs">
                        <Icon icon="solar:sad-circle-linear" className="text-2xl mb-1 d-block mx-auto text-neutral-400" />
                        No categories match "{subCategorySearch}". Try a different keyword or group filter.
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                          gap: "12px",
                        }}
                      >
                        {filteredSubCategories.map((cat) => {
                          const catVal = cat.label_de || cat.label;
                          const isChecked = (formData.preset_sublocation_categories || []).includes(catVal);
                          return (
                            <div
                              key={cat.id || cat.type_key}
                              onClick={() => handleToggleSubCategory(catVal)}
                              className={`p-10 radius-8 border transition-all cursor-pointer d-flex align-items-start gap-2 ${
                                isChecked
                                  ? "bg-primary-50 border-primary-400 text-primary-900 shadow-xs"
                                  : "bg-white border-neutral-200 hover-bg-neutral-50 text-dark"
                              }`}
                              style={{ userSelect: "none", minWidth: 0, overflow: "hidden" }}
                            >
                              <input
                                type="checkbox"
                                className="form-check-input mt-1 flex-shrink-0 cursor-pointer"
                                checked={isChecked}
                                onChange={() => {}}
                              />
                              <div className="flex-grow-1" style={{ minWidth: 0, overflow: "hidden" }}>
                                <div className="d-flex align-items-center justify-content-between gap-1 mb-1">
                                  <span
                                    className="text-xs fw-semibold text-truncate d-block"
                                    style={{ minWidth: 0 }}
                                    title={cat.label_de || cat.label}
                                  >
                                    {cat.label_de || cat.label}
                                  </span>
                                  {isChecked && (
                                    <span className="text-primary-600 flex-shrink-0 ms-1" title="Selected">
                                      <Icon icon="solar:check-circle-bold" className="text-sm" />
                                    </span>
                                  )}
                                </div>
                                <div
                                  className="text-xxs text-secondary-light text-truncate mb-6"
                                  style={{ minWidth: 0 }}
                                  title={cat.label}
                                >
                                  {cat.label}
                                </div>
                                {cat.category_group && (
                                  <div>
                                    <span
                                      className="badge bg-neutral-100 text-secondary-light px-6 py-2 radius-4 text-xxs d-inline-block text-truncate border border-neutral-200"
                                      style={{ maxWidth: "100%", verticalAlign: "middle" }}
                                      title={cat.category_group}
                                    >
                                      {cat.category_group}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* TAB 4: Geofence and Boundaries Outline */}
              {activeTab === "Geofence and Boundaries Outline" && (
                <div>
                  <div className="d-flex align-items-center justify-content-between mb-16 pb-12 border-bottom flex-wrap gap-2">
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <div className="w-36-px h-36-px bg-primary-50 text-primary-600 radius-8 d-flex align-items-center justify-content-center">
                          <Icon icon="tabler:polygon" className="text-xl" />
                        </div>
                        <div>
                          <h6 className="text-sm fw-bold mb-0">Geofence and Boundaries Outline</h6>
                          <p className="text-xs text-secondary-light mb-0">
                            Draw polygons on the satellite map or use the fine-tuned coordinate nudge controls to align the exact geofence boundaries.
                          </p>
                        </div>
                      </div>
                    </div>
                    <span className="badge bg-primary-subtle text-primary-700 px-10 py-6 radius-6 text-xs fw-semibold">
                      Interactive Map & GPS Fine Tuning
                    </span>
                  </div>

            <div className="row g-4">
              {/* Left Column: Coordinates Table & Controls */}
              <div className="col-xl-5 col-lg-5">
                <div className="p-20 radius-12 bg-white border h-100 d-flex flex-column justify-content-between" style={{ minHeight: "600px" }}>
                  <div>
                    {/* Header Bar */}
                    <div className="d-flex align-items-center justify-content-between mb-16 pb-12 border-bottom">
                      <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-primary-subtle text-primary-600 radius-6 px-10 py-6 text-xs fw-semibold">
                          {currentPoints.length} {currentPoints.length === 1 ? "Vertex" : "Vertices"}
                        </span>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        {currentPoints.length > 0 && (
                          <button
                            type="button"
                            onClick={handleClearBoundary}
                            className="btn btn-xs btn-outline-danger py-1 px-8 radius-4 text-xs d-inline-flex align-items-center gap-1"
                            title="Clear boundary"
                          >
                            <Icon icon="solar:trash-bin-trash-bold" />
                            <span>Clear</span>
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={handleSaveOutline}
                          disabled={saveLoading}
                          className={`btn btn-xs py-1 px-10 radius-4 text-xs d-inline-flex align-items-center gap-1 fw-semibold ${
                            hasFormChanged() ? "btn-primary shadow-xs" : "btn-outline-primary"
                          }`}
                          title="Save changes done to boundary"
                        >
                          {saveLoading ? (
                            <Icon icon="line-md:loading-twotone-loop" />
                          ) : (
                            <Icon icon="solar:diskette-bold" />
                          )}
                          <span>Save Outline</span>
                        </button>
                      </div>
                    </div>

                    {/* Multi-Polygon Selector & Add Shape */}
                    {allPolys.length > 0 && (
                      <div className="d-flex align-items-center gap-2 mb-12 overflow-x-auto pb-1 flex-nowrap" style={{ scrollbarWidth: "thin" }}>
                        {allPolys.map((poly, pIdx) => {
                          const isPolyActive = activeIndex === pIdx;
                          return (
                            <button
                              key={pIdx}
                              type="button"
                              className={`btn btn-sm text-nowrap flex-shrink-0 d-inline-flex align-items-center justify-content-center gap-1 px-10 radius-6 text-xs fw-semibold ${
                                isPolyActive
                                  ? "text-white shadow-xs"
                                  : "btn-outline-primary bg-white text-primary-600 border"
                              }`}
                              style={{
                                height: "34px",
                                minHeight: "34px",
                                maxHeight: "34px",
                                lineHeight: "1",
                                whiteSpace: "nowrap",
                                backgroundColor: isPolyActive ? "#f97316" : "#ffffff",
                                borderColor: isPolyActive ? "#ea580c" : "#bfdbfe",
                                color: isPolyActive ? "#ffffff" : "#2563eb",
                                flexShrink: 0
                              }}
                              onClick={() => {
                                setActivePolyIndex(pIdx);
                                setSelectedVertexIndex(0);
                              }}
                            >
                              <span
                                className="radius-circle d-inline-block flex-shrink-0"
                                style={{
                                  width: "7px",
                                  height: "7px",
                                  backgroundColor: isPolyActive ? "#ffffff" : "#0d6efd"
                                }}
                              />
                              <span>Shape {pIdx + 1}</span>
                              <span className="opacity-75 font-monospace">({poly.length} pts)</span>
                            </button>
                          );
                        })}

                        {/* Listview Add Shape Button */}
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary text-nowrap flex-shrink-0 d-inline-flex align-items-center justify-content-center gap-1 px-10 radius-6 text-xs fw-semibold border-dashed"
                          style={{
                            height: "34px",
                            minHeight: "34px",
                            maxHeight: "34px",
                            lineHeight: "1",
                            whiteSpace: "nowrap",
                            flexShrink: 0
                          }}
                          onClick={handleAddShape}
                          title="Add an additional shape"
                        >
                          <Icon icon="solar:add-circle-linear" style={{ fontSize: "15px" }} />
                          <span>+ Add Shape</span>
                        </button>
                      </div>
                    )}

                    {/* Step Adjuster Controls */}
                    <div className="d-flex align-items-center mb-12 bg-neutral-50 p-8 radius-8 border flex-wrap gap-2">
                      <span className="text-xs fw-semibold text-secondary-light">Nudge Step:</span>
                      <div className="d-flex align-items-center gap-1">
                        {[
                          { label: "±1m", value: 0.00001, title: "±0.00001 deg (~1.1m)" },
                          { label: "±5m", value: 0.00005, title: "±0.00005 deg (~5.5m)" },
                          { label: "±10m", value: 0.0001, title: "±0.00010 deg (~11m)" },
                          { label: "±10cm", value: 0.000001, title: "±0.000001 deg (~11cm)" }
                        ].map((stepOpt) => (
                          <button
                            key={stepOpt.value}
                            type="button"
                            title={stepOpt.title}
                            className={`btn btn-xs py-1 px-8 radius-4 text-xs fw-medium ${
                              coordStep === stepOpt.value
                                ? "btn-primary shadow-xs"
                                : "btn-outline-neutral text-secondary-light bg-white border"
                            }`}
                            onClick={() => setCoordStep(stepOpt.value)}
                          >
                            {stepOpt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Vertices Coordinates Table */}
                    {currentPoints.length > 0 ? (
                      <div className="table-responsive border radius-8" style={{ maxHeight: "360px", overflowY: "auto" }}>
                        <table className="table table-sm table-hover align-middle mb-0 text-xs">
                          <thead className="table-light sticky-top">
                            <tr>
                              <th className="text-center py-2 px-1 text-secondary-light fw-semibold" style={{ width: "38px" }}>#</th>
                              <th className="py-2 px-2 text-secondary-light fw-semibold">Coordinates (Lat / Lng)</th>
                              <th className="text-center py-2 px-1 text-secondary-light fw-semibold" style={{ width: "48px" }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentPoints.map((pt, ptIdx) => {
                              const isSelected = selectedVertexIndex === ptIdx;
                              return (
                                <tr
                                  key={`pt-${ptIdx}`}
                                  className={isSelected ? "table-primary" : ""}
                                  onClick={() => {
                                    setSelectedVertexIndex(ptIdx);
                                    if (mapRef.current && pt.lat && pt.lng) {
                                      mapRef.current.panTo({ lat: Number(pt.lat), lng: Number(pt.lng) });
                                    }
                                  }}
                                  style={{ cursor: "pointer", transition: "background-color 0.15s ease" }}
                                >
                                  <td className="text-center py-2 px-1 fw-bold align-middle">
                                    <span className={`badge radius-4 px-2 py-1 ${isSelected ? "bg-primary text-white" : "bg-neutral-100 text-dark"}`}>
                                      {ptIdx + 1}
                                    </span>
                                  </td>
                                  <td className="py-2 px-2" onClick={(e) => e.stopPropagation()}>
                                    {/* Latitude Row */}
                                    <div className="d-flex align-items-center gap-1 mb-1">
                                      <span
                                        className={`badge font-monospace py-1 px-1 fw-semibold text-center ${
                                          isSelected ? "bg-primary-subtle text-primary-700" : "bg-neutral-200 text-secondary-light"
                                        }`}
                                        style={{ width: "32px", fontSize: "9px" }}
                                      >
                                        LAT
                                      </span>
                                      <div className="input-group input-group-sm flex-nowrap flex-grow-1">
                                        <button
                                          type="button"
                                          className="btn btn-outline-secondary px-2 py-0 fw-bold d-flex align-items-center justify-content-center"
                                          style={{ fontSize: "13px", minWidth: "26px", height: "26px", lineHeight: "1" }}
                                          title={`Decrease latitude by ${coordStep}`}
                                          onClick={() => adjustCoordinate(activeIndex, ptIdx, "lat", -coordStep)}
                                        >
                                          -
                                        </button>
                                        <input
                                          type="number"
                                          step="any"
                                          className="form-control form-control-sm px-1 text-center font-monospace"
                                          style={{ fontSize: "11px", height: "26px" }}
                                          value={pt.lat !== undefined ? pt.lat : ""}
                                          onChange={(e) => handleCoordinateChange(activeIndex, ptIdx, "lat", e.target.value)}
                                          onBlur={() => handleCoordinateBlur(activeIndex, ptIdx, "lat")}
                                        />
                                        <button
                                          type="button"
                                          className="btn btn-outline-secondary px-2 py-0 fw-bold d-flex align-items-center justify-content-center"
                                          style={{ fontSize: "13px", minWidth: "26px", height: "26px", lineHeight: "1" }}
                                          title={`Increase latitude by ${coordStep}`}
                                          onClick={() => adjustCoordinate(activeIndex, ptIdx, "lat", coordStep)}
                                        >
                                          +
                                        </button>
                                      </div>
                                    </div>

                                    {/* Longitude Row */}
                                    <div className="d-flex align-items-center gap-1">
                                      <span
                                        className={`badge font-monospace py-1 px-1 fw-semibold text-center ${
                                          isSelected ? "bg-primary-subtle text-primary-700" : "bg-neutral-200 text-secondary-light"
                                        }`}
                                        style={{ width: "32px", fontSize: "9px" }}
                                      >
                                        LNG
                                      </span>
                                      <div className="input-group input-group-sm flex-nowrap flex-grow-1">
                                        <button
                                          type="button"
                                          className="btn btn-outline-secondary px-2 py-0 fw-bold d-flex align-items-center justify-content-center"
                                          style={{ fontSize: "13px", minWidth: "26px", height: "26px", lineHeight: "1" }}
                                          title={`Decrease longitude by ${coordStep}`}
                                          onClick={() => adjustCoordinate(activeIndex, ptIdx, "lng", -coordStep)}
                                        >
                                          -
                                        </button>
                                        <input
                                          type="number"
                                          step="any"
                                          className="form-control form-control-sm px-1 text-center font-monospace"
                                          style={{ fontSize: "11px", height: "26px" }}
                                          value={pt.lng !== undefined ? pt.lng : ""}
                                          onChange={(e) => handleCoordinateChange(activeIndex, ptIdx, "lng", e.target.value)}
                                          onBlur={() => handleCoordinateBlur(activeIndex, ptIdx, "lng")}
                                        />
                                        <button
                                          type="button"
                                          className="btn btn-outline-secondary px-2 py-0 fw-bold d-flex align-items-center justify-content-center"
                                          style={{ fontSize: "13px", minWidth: "26px", height: "26px", lineHeight: "1" }}
                                          title={`Increase longitude by ${coordStep}`}
                                          onClick={() => adjustCoordinate(activeIndex, ptIdx, "lng", coordStep)}
                                        >
                                          +
                                        </button>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="text-center py-2 px-1 align-middle" onClick={(e) => e.stopPropagation()}>
                                    <div className="d-flex flex-column align-items-center justify-content-center gap-1">
                                      <button
                                        type="button"
                                        className={`btn btn-sm p-0 radius-4 d-flex align-items-center justify-content-center ${
                                          isSelected ? "btn-primary text-white" : "btn-outline-primary"
                                        }`}
                                        style={{ width: "24px", height: "24px" }}
                                        title="Pan map to this vertex"
                                        onClick={() => {
                                          setSelectedVertexIndex(ptIdx);
                                          if (mapRef.current && pt.lat && pt.lng) {
                                            mapRef.current.panTo({ lat: Number(pt.lat), lng: Number(pt.lng) });
                                            mapRef.current.setZoom(18);
                                          }
                                        }}
                                      >
                                        <Icon icon="solar:map-point-wave-linear" style={{ fontSize: "13px" }} />
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger p-0 radius-4 d-flex align-items-center justify-content-center"
                                        style={{ width: "24px", height: "24px" }}
                                        title="Delete this vertex"
                                        onClick={() => handleRemovePoint(activeIndex, ptIdx)}
                                      >
                                        <Icon icon="solar:trash-bin-trash-bold" style={{ fontSize: "13px" }} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="p-20 radius-8 bg-neutral-50 border text-center my-8">
                        <div className="w-44-px h-44-px bg-primary-50 text-primary-600 radius-circle d-flex align-items-center justify-content-center mx-auto mb-10">
                          <Icon icon="tabler:polygon" className="text-2xl" />
                        </div>
                        <h6 className="fw-semibold text-dark mb-4 text-sm">No Outline Points Yet</h6>
                        <p className="text-xs text-secondary-light mb-12" style={{ maxWidth: "320px", margin: "0 auto" }}>
                          Draw a boundary on the map using the polygon drawing tool, or create a starter geofence around the map center.
                        </p>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary radius-6 d-inline-flex align-items-center gap-2 py-1 px-12 text-xs"
                          onClick={() => handleAddPoint(0)}
                        >
                          <Icon icon="solar:square-academic-cap-2-bold" />
                          <span>Create Starter Outline Around Center</span>
                        </button>
                      </div>
                    )}

                    {/* Quick Toolbar */}
                    <div className="d-flex align-items-center justify-content-between mt-12 pt-8 border-top">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary radius-6 d-inline-flex align-items-center gap-1 py-1 px-10 text-xs"
                        onClick={() => handleAddPoint(activeIndex)}
                      >
                        <Icon icon="solar:add-circle-linear" />
                        <span>Add Vertex</span>
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-link text-secondary-light text-decoration-none p-0 text-xs"
                        onClick={() => setShowJsonRaw((prev) => !prev)}
                      >
                        <Icon icon={showJsonRaw ? "solar:eye-closed-linear" : "solar:eye-linear"} className="me-1" />
                        {showJsonRaw ? "Hide Raw JSON" : "View Raw JSON"}
                      </button>
                    </div>

                    {showJsonRaw && (
                      <div className="mt-10">
                        <textarea
                          className="form-control radius-8 bg-neutral-50 text-secondary-light"
                          readOnly
                          rows={4}
                          style={{ fontFamily: "monospace", fontSize: 10 }}
                          value={formData.geometrics_outline || ""}
                        />
                      </div>
                    )}
                  </div>

                  {/* Boundary Status & Recenter */}
                  {currentPoints.length > 0 && (
                    <div className="mt-12 pt-8 border-top d-flex align-items-center justify-content-between text-xs text-secondary-light">
                      <span>Total Vertices: {currentPoints.length}</span>
                      <button
                        type="button"
                        onClick={() => fitMapToBoundary(formData.geometrics_outline)}
                        className="btn btn-sm btn-link p-0 text-primary-600 text-decoration-none text-xs fw-semibold"
                      >
                        Fit Map to Boundary
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column: Interactive Map */}
              <div className="col-xl-7 col-lg-7">
                <div className="border radius-12 overflow-hidden shadow-sm h-100 d-flex flex-column" style={{ minHeight: "600px" }}>
                  {/* Map Header Toolbar with Autocomplete & Quick Save */}
                  <div className="d-flex align-items-center justify-content-between px-16 py-10 bg-white border-bottom gap-2">
                    <div className="icon-field flex-grow-1" style={{ maxWidth: "380px" }}>
                      <span className="icon">
                        <Icon icon="solar:magnifer-linear" />
                      </span>
                      <Autocomplete
                        onLoad={(a) => (autocompleteRef.current = a)}
                        onPlaceChanged={onPlaceChanged}
                      >
                        <input
                          className="form-control form-control-sm radius-8 ps-36 text-xs"
                          placeholder="Search for a city, district, or address..."
                        />
                      </Autocomplete>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        onClick={handleAddShape}
                        className="btn btn-xs btn-primary text-white py-1 px-10 radius-4 text-xs d-flex align-items-center gap-1 fw-semibold shadow-xs"
                        title="Add an additional shape to the map"
                      >
                        <Icon icon="solar:add-circle-bold" />
                        <span>+ Add Shape</span>
                      </button>
                      {currentPoints.length > 0 && (
                        <button
                          type="button"
                          onClick={() => fitMapToBoundary(formData.geometrics_outline)}
                          className="btn btn-xs btn-outline-neutral text-secondary-light bg-white border py-1 px-8 radius-4 text-xs d-flex align-items-center gap-1"
                          title="Fit map to boundary"
                        >
                          <Icon icon="solar:maximize-square-linear" />
                          <span>Fit Outline</span>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={handleSaveOutline}
                        disabled={saveLoading}
                        className={`btn btn-xs py-1 px-12 radius-4 text-xs d-inline-flex align-items-center gap-1 fw-semibold ${
                          hasFormChanged() ? "btn-primary shadow-xs" : "btn-outline-primary"
                        }`}
                        title="Save changes to boundary"
                      >
                        {saveLoading ? (
                          <Icon icon="line-md:loading-twotone-loop" />
                        ) : (
                          <Icon icon="solar:diskette-bold" />
                        )}
                        <span>Save Outline</span>
                      </button>
                    </div>
                  </div>

                  {/* Map Container */}
                  <div className="flex-grow-1 position-relative" style={{ minHeight: "540px" }}>
                    {isLoaded ? (
                      <GoogleMap
                        mapContainerStyle={{ width: "100%", height: "100%", minHeight: "540px" }}
                        center={DEFAULT_CENTER}
                        zoom={12}
                        mapTypeId="satellite"
                        onLoad={onMapLoad}
                        options={{
                          mapTypeId: "satellite",
                          tilt: 0,
                          rotateControl: false,
                          zoomControl: true,
                          mapTypeControl: false,
                          streetViewControl: false,
                          fullscreenControl: true,
                          styles: [
                            {
                              featureType: "all",
                              elementType: "labels",
                              stylers: [{ visibility: "off" }]
                            }
                          ]
                        }}
                      >
                        <DrawTools
                          key={outlineDrawVersion}
                          data={formData.geometrics_outline}
                          onChange={(json) =>
                            setFormData((prev) => ({
                              ...prev,
                              geometrics_outline: json
                            }))
                          }
                          onVertexSelect={(polyIdx, vertIdx) => {
                            setActivePolyIndex(polyIdx);
                            setSelectedVertexIndex(vertIdx);
                          }}
                          activePolyIndex={activeIndex}
                        />
                      </GoogleMap>
                    ) : (
                      <div
                        className="w-100 h-100 radius-12 bg-neutral-100 d-flex align-items-center justify-content-center flex-column"
                        style={{ minHeight: "540px", border: "2px dashed #e3e6ef" }}
                      >
                        <Icon icon="line-md:loading-twotone-loop" className="text-primary-600 text-4xl mb-12" />
                        <p className="fw-medium text-secondary-light">Loading Geofence Engine...</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
                </div>
              )}

              {/* SECTION NAVIGATION BUTTONS */}
              <div className="d-flex justify-content-between align-items-center mt-32 pt-20 border-top">
                {tabIndex > 0 ? (
                  <button
                    type="button"
                    onClick={() => setActiveTab(TABS[tabIndex - 1].id)}
                    className="btn btn-outline-secondary d-inline-flex align-items-center gap-2 px-16 py-10 radius-8 fw-semibold text-xs"
                  >
                    <Icon icon="solar:arrow-left-linear" />
                    <span>Previous: {TABS[tabIndex - 1].id}</span>
                  </button>
                ) : <div />}

                {tabIndex < TABS.length - 1 ? (
                  <button
                    type="button"
                    onClick={() => setActiveTab(TABS[tabIndex + 1].id)}
                    className="btn btn-primary d-inline-flex align-items-center gap-2 px-16 py-10 radius-8 fw-semibold text-xs"
                  >
                    <span>Next: {TABS[tabIndex + 1].id}</span>
                    <Icon icon="solar:arrow-right-linear" />
                  </button>
                ) : <div />}
              </div>

            </div>
          </div>
        </div>

        {/* STICKY BOTTOM ACTION BAR */}
        <FloatingFormActions
          isDirty={hasFormChanged()}
          cancelUrl="/locations-top"
          cancelText="Discard"
          saveText={id ? "Update Top Location" : "Save Top Location"}
          loading={saveLoading}
          saveIcon="solar:check-read-linear"
          onSubmit={handleSubmit}
        />
      </form>
    </div>
  );
};

export default LocationsTopFormPage;